// src/components/chat/ChatTitle.jsx
import React, { useMemo } from "react";

/**
 * Будує "красивий" заголовок чату з першого повідомлення.
 * Приклади:
 *  - "Я шукаю лагер пиво" -> "Пошук лагер пива"
 *  - "покажи акції" -> "Акції"
 *  - "хочу ipa" -> "IPA варіанти"
 */

function normalizeText(s = "") {
  return String(s || "")
    .replace(/\s+/g, " ")
    .trim();
}

function stripPunctuation(s = "") {
  // залишаємо літери/цифри/пробіли (укр/лат)
  return String(s || "")
    .replace(/[^\p{L}\p{N}\s]/gu, "")
    .replace(/\s+/g, " ")
    .trim();
}

function titleCaseFirst(s = "") {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function buildChatTitleFromFirstMessage(message) {
  const raw = normalizeText(message);
  if (!raw) return "Новий чат";

  const clean = stripPunctuation(raw);
  if (!clean) return "Новий чат";

  const lower = clean.toLowerCase();

  // ✅ спеціальні швидкі правила (можеш розширювати)
  const hasSale = /\b(акц(ія|ії|ій)|знижк(а|и)|розпродаж)\b/i.test(clean);
  if (hasSale) return "Акції";

  const hasNew = /\b(новинк(а|и)|нове|новинки)\b/i.test(clean);
  if (hasNew) return "Новинки";

  const hasIpa = /\bipa\b/i.test(clean);
  if (hasIpa) return "IPA варіанти";

  const hasLager = /\blager\b/i.test(clean) || /\bлагер\b/i.test(lower);
  if (hasLager) return "Пошук лагер пива";

  // ✅ загальне правило: "Пошук ..." якщо є "шукаю/покажи/хочу/порадь"
  if (/\b(шукаю|покажи|хочу|порадь|підкажи|дай)\b/i.test(lower)) {
    const words = clean.split(" ").slice(0, 4);
    // прибираємо службові слова на старті
    const stop = new Set(["я", "мені", "будь", "ласка", "покажи", "дай", "хочу", "шукаю", "порадь", "підкажи"]);
    while (words.length && stop.has(words[0].toLowerCase())) words.shift();

    const tail = words.slice(0, 3).join(" ");
    if (tail) return `Пошук ${tail.toLowerCase()}`;
  }

  // ✅ fallback: перші 4 слова
  const short = clean.split(" ").slice(0, 4).join(" ");
  return titleCaseFirst(short);
}

export default function ChatTitle({ chat }) {
  const title = useMemo(() => {
    // 1) якщо вже є нормальний title — показуємо його
    const t = normalizeText(chat?.title);
    if (t && t !== "Новий чат") return t;

    // 2) пробуємо витягнути перше повідомлення з різних можливих полів
    const firstMsg =
      chat?.first_message ||
      chat?.firstMessage ||
      chat?.preview ||
      chat?.last_user_message ||
      chat?.lastMessage ||
      chat?.message ||
      "";

    return buildChatTitleFromFirstMessage(firstMsg);
  }, [chat]);

  return <>{title}</>;
}